import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { gql, useQuery } from 'urql';
import { RoleName, Terminal } from '../types';
import * as H from 'history';
import { filter, includes, map, nth, size } from 'lodash';
import { terminalHandlingsPages } from './TerminalHandlings';
import { useTranslation } from 'react-i18next';
import { MatchPath } from '.';
import { NavItem, NavLink } from 'reactstrap';
import { Link } from 'react-router-dom';
import { useCurrentUser } from '../hooks';

type NavigationLinkProps = PropsWithChildren<{
    to: H.LocationDescriptor<H.LocationState> | ((location: H.Location<H.LocationState>) => H.LocationDescriptor<H.LocationState>)
}>;

const NavigationLink = ({ to, children }: NavigationLinkProps) => {
    return <MatchPath to={to} render={(to, match) =>
        <NavItem className={match ? "active" : undefined}>
            <NavLink tag={Link} className="nav-link" to={to}>{children}</NavLink>
        </NavItem>
    } />
}

type TerminalNavigationProps = {
    id: number|null
};

const terminalNavigationQuery = gql`
query terminal($id: Int!) {
    terminal (id: $id) {
        id
        name
        slug
        port
    }
}
`;

const TerminalNavigation = ({ id: defaultId }: TerminalNavigationProps) => {

    const [id, setId] = useState(defaultId);

    useEffect(() => {
        if (defaultId !== null) {
            setId(defaultId);
        }
    }, [setId, defaultId])

    const [result] = useQuery<{ terminal: Terminal }, { id: number }>({
        query: terminalNavigationQuery,
        variables: { id: id??0 },
        pause: id === null
    });

    const { data/*, fetching, error*/ } = result;
    const { t } = useTranslation('common');

    const terminal = useMemo(() => data?.terminal, [data]);

    const { currentUser, hasRoleForTerminal, isSuperuser } = useCurrentUser();

    useEffect(() => {
        if (size(currentUser?.terminals) === 1) {
            setId((id) => nth(currentUser?.terminals, 0)?.id ?? id);
        }
    }, [setId, currentUser])

    const canAddHandling = useMemo(() => {
        if (id === null) {
            return false;
        }

        return isSuperuser || hasRoleForTerminal(RoleName.USER, id) || hasRoleForTerminal(RoleName.ADMIN, id)
    }, [id, isSuperuser, hasRoleForTerminal]);

    const navMenuLinks = useMemo(() => {
        if (currentUser === null || terminal?.id !== id) {
            return null;
        }

        const { slug, port, name } = terminal;

        const pages = name === 'REBORA' ? filter(terminalHandlingsPages, ({ key }) => key !== 'transits') : terminalHandlingsPages;

        const links = map(pages, (page) => ({
            to: `/${slug}/${t(`terminal.${page.key}.slug`)}`,
            content: t(`terminal.${page.key}.link`)
        }));

        if (port === 'LA SPEZIA') {
            links.push({
                to: `/${slug}/${t('approvazione')}`,
                content: t('Approvazione')
            });
        }

        if (port === 'GENOVA'||name === 'SINTERMAR') {
            links.push({
                to: `/${slug}/${t('terminal.storageReport.slug')}`,
                content: t('terminal.storageReport.link')
            });
        }

        if (canAddHandling && (includes(['REBORA','MEDCENTER','VECON','SAN CATALDO','RTC'], name) || terminal.port === 'TRIESTE')) {
            links.push({
                to: `/${slug}/${t('nuovo-previsionale')}`,
                content: t('Nuovo previsionale')
            });
        }

        links.push({
            to: `/${slug}/${t('rischio')}`,
            content: t('Rischio')
        });

        if (name === 'TDT') {
            links.push({
                to: `/${slug}/${t('invio-dati')}`,
                content: t('Invio Dati')
            });
        }

        links.push({
            to: `/${slug}/${t('segregazione')}`,
            content: t('Segregazione')
        });

        links.push({
            to: `/${slug}/${t('cerca-container')}`,
            content: t('Cerca container')
        });

        if (canAddHandling) {
            links.push({
                to: `/${slug}/${t('importazione-pmis')}`,
                content: t('Importa da PMIS')
            });
        }

        links.push({
            to: `/${slug}/${t('isps-code')}`,
            content: t('Codice ISPS')
        });

        return links;
    }, [terminal, currentUser, id, t]);

    if (navMenuLinks === null) {
        return null;
    }

    return <ul className="navbar-nav flex-grow">{
        navMenuLinks.map(({ to, content }) => <NavigationLink key={to} to={to}>{content}</NavigationLink>)
    }</ul>
}

export default TerminalNavigation;